import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type ScrollRevealAnimation = 'fade' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale' | 'fade-only';

interface ScrollRevealOptions {
  animation: ScrollRevealAnimation;
  delay: number;
  threshold: number;
  once: boolean;
  stagger: boolean;
}

@customElement('pm-scroll-reveal')
export class PmScrollReveal extends LitElement {
  @property({ type: String }) animation: ScrollRevealAnimation = 'fade';
  @property({ type: Number }) delay: number = 0;
  @property({ type: Number }) threshold: number = 100;
  @property({ type: Boolean }) once: boolean = true;
  @property({ type: Boolean }) stagger: boolean = false;

  @state() private _observer: IntersectionObserver | null = null;
  @state() private _revealed: boolean = false;
  @state() private _staggerIndex: number = 0;

  static styles = css`
    :host {
      display: block;
      opacity: 0;
      transition: opacity 0.8s ease, transform 0.8s ease;
    }

    /* Animation states */
    :host([animation="fade"]) {
      transform: translateY(30px);
    }

    :host([animation="slide-up"]) {
      transform: translateY(50px);
    }

    :host([animation="slide-left"]) {
      transform: translateX(-50px);
    }

    :host([animation="slide-right"]) {
      transform: translateX(50px);
    }

    :host([animation="scale"]) {
      transform: scale(0.8);
    }

    :host([animation="fade-only"]) {
      transform: none;
    }

    /* Revealed state */
    :host(.revealed) {
      opacity: 1;
      transform: translateY(0) translateX(0) scale(1);
    }

    /* Stagger support */
    :host(.stagger) {
      transition-delay: var(--stagger-delay, 0ms);
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
      :host([animation="slide-left"]),
      :host([animation="slide-right"]) {
        transform: translateY(30px);
      }
    }
  `;

  constructor() {
    super();
    this.animation = 'fade';
    this.delay = 0;
    this.threshold = 100;
    this.once = true;
    this.stagger = false;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._setupStagger();
    this._setupObserver();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._cleanupObserver();
  }

  private _setupStagger(): void {
    if (this.stagger) {
      // Find sibling scroll-reveal elements for stagger calculation
      const parent = this.parentElement;
      if (parent) {
        const siblings = Array.from(parent.querySelectorAll('pm-scroll-reveal[stagger]'));
        this._staggerIndex = siblings.indexOf(this);
        
        // Set CSS custom property for stagger delay
        const staggerDelay = this.delay + (this._staggerIndex * 150); // 150ms between items
        this.style.setProperty('--stagger-delay', `${staggerDelay}ms`);
        this.classList.add('stagger');
      }
    }
  }

  private _setupObserver(): void {
    // Use Intersection Observer if available, fallback to scroll listener
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this._revealed) {
              this._reveal();
            }
          });
        },
        {
          rootMargin: `${this.threshold}px`,
          threshold: 0.1
        }
      );

      this._observer.observe(this);
    } else {
      // Fallback for older browsers
      this._setupScrollListener();
    }
  }

  private _setupScrollListener(): void {
    let ticking = false;
    
    const checkVisibility = (): void => {
      if (this._revealed && this.once) return;
      
      const rect = this.getBoundingClientRect();
      const isVisible = (
        rect.top >= -100 &&
        rect.top <= (window.innerHeight - this.threshold) &&
        rect.bottom >= 0
      );

      if (isVisible && !this._revealed) {
        this._reveal();
      }
    };

    const onScroll = (): void => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    
    // Initial check
    setTimeout(checkVisibility, 100);
  }

  private _cleanupObserver(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  private _reveal(): void {
    if (this._revealed && this.once) return;

    this._revealed = true;
    
    // Apply reveal class with delay
    setTimeout(() => {
      this.classList.add('revealed');
      
      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('pm-scroll-reveal:revealed', {
        bubbles: true,
        composed: true,
        detail: {
          animation: this.animation,
          element: this
        }
      }));
    }, this.delay);

    // Disconnect observer if once=true
    if (this.once && this._observer) {
      this._observer.disconnect();
    }
  }

  /**
   * Public method to manually trigger reveal
   */
  public reveal(): void {
    this._reveal();
  }

  /**
   * Public method to reset animation
   */
  public reset(): void {
    this._revealed = false;
    this.classList.remove('revealed');
    if (this._observer) {
      this._observer.observe(this);
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}

// Export for use in other modules
export default PmScrollReveal;